import { useCallback, useEffect, useRef, useState } from "react";
import { OperationResult, useMutation } from "urql";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useHotkeys } from "react-hotkeys-hook";
import { useMutationSaveStatus } from "./useMutationSaveStatus";
import { MutationSaveStatus } from "./MutationSaveStatus";

interface UseAutoSaveMutationOptions<
  TData,
  TVariables extends Record<string, unknown>,
> {
  value: string | null;
  mutationDocument: TypedDocumentNode<TData, TVariables>;
  buildVariables: (validatedValue: string | null) => TVariables;
  validateAndTransform: (newValue: string) => string | null | "invalid";
  onMutationCompleted?: (r: OperationResult<TData, TVariables>) => void;
  hotkey: string;
  hotkeyFormTag: "input" | "textarea";
  onContentPreviewUpdate?: (value: string | null) => void;
}

interface UseAutoSaveMutationReturn {
  inputValue: string;
  setInputValue: (value: string) => void;
  isValid: boolean;
  isFocused: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  handleFocus: () => void;
  handleBlur: () => void;
  handleSave: () => void;
  handleCancel: () => void;
  saveStatus: MutationSaveStatus;
}

/**
 * Custom Hook für Auto-Save Mutation Logik
 *
 * Dieser Hook extrahiert die gemeinsame Logik für Input-Komponenten mit GraphQL-Mutation Support:
 * - State-Management (inputValue, isValid, isFocused)
 * - Event-Handler (handleBlur, handleSave, handleCancel)
 * - URQL-Execution
 * - Hotkey-Unterstützung
 */
export function useAutoSaveMutation<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
>(
  options: UseAutoSaveMutationOptions<TData, TVariables>,
): UseAutoSaveMutationReturn {
  const {
    value,
    mutationDocument,
    buildVariables,
    validateAndTransform,
    onMutationCompleted = () => {},
    hotkey,
    hotkeyFormTag,
    onContentPreviewUpdate,
  } = options;

  const [, executeMutation] = useMutation(mutationDocument);
  const {
    status: saveStatus,
    markSaving,
    markSettled,
  } = useMutationSaveStatus();

  const isValueValid = useCallback(
    (val: string): boolean => {
      const result = validateAndTransform(val);
      return result !== "invalid";
    },
    [validateAndTransform],
  );

  const [inputValue, setInputValue] = useState(value ?? "");
  const [isValid, setIsValid] = useState(isValueValid(value ?? ""));
  const [isFocused, setIsFocused] = useState(false);
  const previousValueRef = useRef(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);
  const savedRef = useRef(false);

  // Update inputValue when value changes (e.g., when new data is loaded from server)
  useEffect(() => {
    setInputValue(value ?? "");
    previousValueRef.current = value ?? "";
  }, [value]);

  // Update validation state and content preview
  useEffect(() => {
    const valid = isValueValid(inputValue);
    setIsValid(valid);
    if (valid && onContentPreviewUpdate) {
      onContentPreviewUpdate(inputValue);
    }
  }, [inputValue, isValueValid, onContentPreviewUpdate]);

  const saveMutation = useCallback(() => {
    const validatedValue = validateAndTransform(inputValue);

    // Don't save if validation failed
    if (validatedValue === "invalid") {
      return;
    }

    // Don't save if value hasn't changed
    if (validatedValue === previousValueRef.current) {
      return;
    }

    const variables = buildVariables(validatedValue);
    markSaving();
    executeMutation(variables)
      .then((result) => {
        console.debug("Mutation result", result);
        if (!result.error) {
          previousValueRef.current = validatedValue ?? "";
        }
        markSettled(!!result.error);
        onMutationCompleted(result);
      })
      .catch((err) => {
        console.error("Mutation threw", err);
        markSettled(true);
      });
  }, [
    inputValue,
    validateAndTransform,
    buildVariables,
    executeMutation,
    onMutationCompleted,
    markSaving,
    markSettled,
  ]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);

    // Skip save if cancel button was clicked
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }

    // Skip save if already saved (e.g., by hotkey)
    if (savedRef.current) {
      savedRef.current = false;
      return;
    }

    saveMutation();
  }, [saveMutation]);

  const handleSave = useCallback(() => {
    // Set flag to prevent handleBlur from saving when blur is triggered
    savedRef.current = true;
    saveMutation();
    setIsFocused(false);
    inputRef.current?.blur();
  }, [saveMutation]);

  const handleCancel = useCallback(() => {
    // Prevent handleBlur from saving when blur is triggered
    cancelledRef.current = true;
    setInputValue(previousValueRef.current);
    setIsFocused(false);
    inputRef.current?.blur();
  }, []);

  // Hotkeys: configurable save hotkey, Escape to cancel
  useHotkeys(
    hotkey,
    (e) => {
      e.preventDefault();
      handleSave();
    },
    {
      enableOnFormTags: [hotkeyFormTag],
      enabled: isFocused,
    },
    [handleSave, isFocused],
  );

  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      handleCancel();
    },
    {
      enableOnFormTags: [hotkeyFormTag],
      enabled: isFocused,
    },
    [handleCancel, isFocused],
  );

  return {
    inputValue,
    setInputValue,
    isValid,
    isFocused,
    inputRef,
    handleFocus,
    handleBlur,
    handleSave,
    handleCancel,
    saveStatus,
  };
}
